'use client'

import { MailIcon } from 'lucide-react'
import { useTranslations } from 'next-intl'
import { useState, useTransition } from 'react'
import { toast } from 'sonner'
import { requestOtpAction, signInWithOtpAction, type SignInError } from '@/app/(auth)/sign-in/actions'
import { AuthSubmitButton } from '@/components/auth/auth-submit-button'
import { IconInput } from '@/components/auth/icon-input'
import { Button } from '@/components/ui/button'
import { Field, FieldDescription, FieldError, FieldLabel } from '@/components/ui/field'
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp'

const OTP_LENGTH = 6

export function EmailOtpForm({ callbackUrl }: { callbackUrl?: string }) {
	const t = useTranslations('auth')
	const [email, setEmail] = useState('')
	const [otp, setOtp] = useState('')
	const [step, setStep] = useState<'email' | 'otp'>('email')
	const [error, setError] = useState<SignInError | null>(null)
	const [isPending, startTransition] = useTransition()

	const requestCode = () => {
		setError(null)
		startTransition(async () => {
			const result = await requestOtpAction(email)
			if (!result.ok) return setError(result.error)
			toast.success(t('otp.sent'))
			setStep('otp')
		})
	}

	const submitCode = () => {
		setError(null)
		startTransition(async () => {
			const result = await signInWithOtpAction({ email, otp, callbackUrl })
			if (!result.ok) setError(result.error)
		})
	}

	if (step === 'email') {
		return (
			<form
				className="flex flex-col gap-4"
				onSubmit={(event) => {
					event.preventDefault()
					requestCode()
				}}
			>
				<Field>
					<FieldLabel htmlFor="email">{t('email.label')}</FieldLabel>
					<IconInput
						icon={MailIcon}
						id="email"
						type="email"
						autoComplete="email"
						required
						placeholder={t('email.placeholder')}
						value={email}
						onChange={(event) => setEmail(event.target.value)}
					/>
					{error && <FieldError>{t(`errors.${error}`)}</FieldError>}
				</Field>
				<AuthSubmitButton pending={isPending}>{t('email.submit')}</AuthSubmitButton>
			</form>
		)
	}

	return (
		<form
			className="flex flex-col gap-4"
			onSubmit={(event) => {
				event.preventDefault()
				submitCode()
			}}
		>
			<Field>
				<FieldLabel htmlFor="otp">{t('otp.label')}</FieldLabel>
				<InputOTP
					id="otp"
					maxLength={OTP_LENGTH}
					value={otp}
					onChange={setOtp}
					autoFocus
					containerClassName="justify-center"
				>
					<InputOTPGroup>
						{Array.from({ length: OTP_LENGTH }, (_, index) => (
							<InputOTPSlot key={index} index={index} />
						))}
					</InputOTPGroup>
				</InputOTP>
				<FieldDescription>{t('otp.hint', { email })}</FieldDescription>
				{error && <FieldError>{t(`errors.${error}`)}</FieldError>}
			</Field>
			<AuthSubmitButton pending={isPending} disabled={otp.length !== OTP_LENGTH}>
				{t('otp.submit')}
			</AuthSubmitButton>
			<div className="flex justify-between text-sm">
				<Button
					type="button"
					variant="link"
					size="sm"
					className="px-0"
					disabled={isPending}
					onClick={requestCode}
				>
					{t('otp.resend')}
				</Button>
				<Button
					type="button"
					variant="link"
					size="sm"
					className="px-0"
					disabled={isPending}
					onClick={() => {
						setOtp('')
						setStep('email')
					}}
				>
					{t('otp.change')}
				</Button>
			</div>
		</form>
	)
}
